// src/pages/CongNoXuatPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, query, orderBy as fbOrderBy } from 'firebase/firestore';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  TablePagination,
  Chip,
  Grid,
  Autocomplete,
  Button,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { startOfMonth, endOfMonth, startOfDay, endOfDay } from 'date-fns';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptIcon from '@mui/icons-material/Receipt';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CancelIcon from '@mui/icons-material/Cancel';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';

// Helper Functions
const formatDateTime = (timestamp) => {
  if (!timestamp) return 'N/A';
  return new Date(timestamp.seconds * 1000).toLocaleString('vi-VN');
};
const formatCurrency = (number) => {
  if (typeof number !== 'number' || isNaN(number)) return '0 ₫';
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(number);
};
const getOrderStatusChip = (status) => {
    switch (status) {
        case 'shipped': return <Chip label="Đã chuyển" color="success" size="small" />;
        case 'cancelled': return <Chip label="Đã hủy" color="error" size="small" />;
        case 'other': return <Chip label="Khác" color="default" size="small" />;
        case 'processing': default: return <Chip label="Đang xử lý" color="info" size="small" />;
    }
};

const StatCard = ({ title, value, icon, color, onClick, selected }) => (
    <Paper 
        elevation={3} 
        sx={{ 
            p: 2, 
            display: 'flex', 
            alignItems: 'center', 
            height: '100%',
            cursor: 'pointer',
            border: selected ? `2px solid ${color || '#1976d2'}` : '2px solid transparent',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
            }
        }}
        onClick={onClick}
    >
        <Box sx={{ mr: 2, color: color || 'text.secondary' }}>{icon}</Box>
        <Box>
            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>{value}</Typography>
            <Typography variant="body2" color="text.secondary">{title}</Typography>
        </Box>
    </Paper>
);


function CongNoXuatPage() {
  const [exports, setExports] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { currentUser } = useAuth();

  const [startDate, setStartDate] = useState(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(endOfMonth(new Date()));
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  
  const canViewAll = currentUser?.roleNames?.some(name => name.toLowerCase() === 'admin') || currentUser?.permissions?.full_access || currentUser?.permissions?.viewAllData;
  const [viewMode, setViewMode] = useState(canViewAll ? 'all' : 'own');

  useEffect(() => {
    const unsubCustomers = onSnapshot(collection(db, 'customers'), (snapshot) => {
      setCustomers(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });

    const q = query(collection(db, 'exports'), fbOrderBy('exportDate', 'desc'));
    const unsubExports = onSnapshot(q, (snapshot) => {
      setExports(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    });

    return () => {
      unsubCustomers();
      unsubExports();
    };
  }, []);

  const activeCustomers = useMemo(() => {
    const customerIdsInExports = new Set(exports.map((exp) => exp.customerId));
    return customers.filter((cus) => customerIdsInExports.has(cus.id));
  }, [exports, customers]);

  const { detailedItems, totalReceivable, statusMetrics } = useMemo(() => {
    const start = startDate ? startOfDay(startDate) : null;
    const end = endDate ? endOfDay(endDate) : null;

    const baseFilteredExports = exports.filter(slip => {
        if (canViewAll && viewMode === 'all') {
            return true;
        }
        return slip.createdBy === currentUser.email; 
    });

    const filteredSlips = baseFilteredExports.filter((slip) => {
      const exportDate = slip.exportDate?.toDate();
      if (!exportDate) return false;
      const dateMatch = start && end ? exportDate >= start && exportDate <= end : true;
      const customerMatch = selectedCustomer ? slip.customerId === selectedCustomer.id : true;
      const currentStatus = slip.status || 'processing';
      const statusMatch = statusFilter === 'all' ? true : currentStatus === statusFilter;
      return dateMatch && customerMatch && statusMatch;
    });

    const allFilteredSlipsInDateRange = baseFilteredExports.filter((slip) => {
        const exportDate = slip.exportDate?.toDate();
        if (!exportDate) return false;
        const dateMatch = start && end ? exportDate >= start && exportDate <= end : true;
        const customerMatch = selectedCustomer ? slip.customerId === selectedCustomer.id : true;
        return dateMatch && customerMatch;
    });

    const metrics = {
        total: allFilteredSlipsInDateRange.length,
        processing: allFilteredSlipsInDateRange.filter(s => (s.status || 'processing') === 'processing').length,
        shipped: allFilteredSlipsInDateRange.filter(s => s.status === 'shipped').length,
        cancelled: allFilteredSlipsInDateRange.filter(s => s.status === 'cancelled').length,
    };

    const items = filteredSlips.flatMap((slip) =>
      (slip.items || []).map((item) => ({
        ...item,
        soDate: slip.soDate,
        shippingDate: slip.shippingDate,
        soNumber: slip.soNumber,
        customerName: slip.customerName,
        shippingFee: slip.shippingFee,
        taxAmount: slip.taxAmount,
        grandTotal: slip.grandTotal,
        status: slip.status,
        createdBy: slip.createdBy,
        slipId: slip.id,
      })),
    );

    const total = filteredSlips.reduce((sum, slip) => sum + (slip.remainingAmount || 0), 0);
    
    return { detailedItems: items, totalReceivable: total, statusMetrics: metrics };
  }, [exports, startDate, endDate, selectedCustomer, statusFilter, currentUser, viewMode, canViewAll]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportExcel = () => {
    if (detailedItems.length === 0) {
      alert('Không có dữ liệu để xuất.');
      return;
    }
    const dataToExport = detailedItems.map((item) => ({
      'Ngày lập SO': formatDateTime(item.soDate),
      'Ngày xuất hàng': formatDateTime(item.shippingDate),
      'Mã đơn (SO)': item.soNumber,
      'Khách hàng': item.customerName,
      'Tên sản phẩm': item.productName,
      'Số lượng': item.quantity,
      'Giá xuất': item.exportPrice,
      'Tiền hàng': item.quantity * item.exportPrice,
      'Tiền thuế': item.taxAmount,
      'Cước vận chuyển': item.shippingFee,
      'Tổng cộng': item.grandTotal,
      'Trạng thái ĐH': getOrderStatusChip(item.status).props.label,
      'Người tạo': item.createdBy,
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'CongNoXuat');
    XLSX.writeFile(
      wb,
      `CongNoPhaiThu_${new Date().toLocaleDateString('vi-VN').replace(/\//g, '-')}.xlsx`,
    );
  };

  if (loading) return <Typography>Đang tải dữ liệu...</Typography>;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Báo Cáo Công Nợ Phải Thu (Xuất Hàng)
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <DatePicker
              label="Từ ngày"
              value={startDate}
              onChange={setStartDate}
              renderInput={(params) => <TextField {...params} fullWidth size="small" />}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <DatePicker
              label="Đến ngày"
              value={endDate}
              onChange={setEndDate}
              renderInput={(params) => <TextField {...params} fullWidth size="small" />}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={6}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportExcel}
              sx={{ width: '100%', height: '100%' }}
            >
              Xuất Excel
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              options={activeCustomers}
              getOptionLabel={(option) => option.name}
              value={selectedCustomer}
              onChange={(event, newValue) => setSelectedCustomer(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Lọc theo Khách Hàng (chỉ hiện KH có đơn hàng)"
                  size="small"
                />
              )}
            />
          </Grid>
          {canViewAll && (
            <Grid item xs={12}>
                <ToggleButtonGroup
                    color="primary"
                    value={viewMode}
                    exclusive
                    onChange={(e, newMode) => { if (newMode !== null) setViewMode(newMode); }}
                    aria-label="Chế độ xem"
                    size="small"
                >
                    <ToggleButton value="own">Xem của tôi</ToggleButton>
                    <ToggleButton value="all">Xem tất cả</ToggleButton>
                </ToggleButtonGroup>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}><StatCard title="Tổng số đơn hàng" value={statusMetrics.total} icon={<ReceiptIcon fontSize="large" />} onClick={() => setStatusFilter('all')} selected={statusFilter === 'all'} /></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard title="Đang xử lý" value={statusMetrics.processing} icon={<HourglassTopIcon fontSize="large" />} color="info.main" onClick={() => setStatusFilter('processing')} selected={statusFilter === 'processing'} /></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard title="Đã chuyển" value={statusMetrics.shipped} icon={<LocalShippingIcon fontSize="large" />} color="success.main" onClick={() => setStatusFilter('shipped')} selected={statusFilter === 'shipped'} /></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard title="Đã hủy" value={statusMetrics.cancelled} icon={<CancelIcon fontSize="large" />} color="error.main" onClick={() => setStatusFilter('cancelled')} selected={statusFilter === 'cancelled'} /></Grid>
      </Grid>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Ngày xuất</TableCell>
                <TableCell>Mã đơn (SO)</TableCell>
                <TableCell>Khách hàng</TableCell>
                <TableCell>Tên sản phẩm</TableCell>
                <TableCell align="right">Số lượng</TableCell>
                <TableCell align="right">Giá xuất</TableCell>
                <TableCell align="right">Tiền hàng</TableCell>
                <TableCell align="right">Tiền thuế</TableCell>
                <TableCell align="right">Cước VC</TableCell>
                <TableCell align="right">Tổng cộng</TableCell>
                <TableCell>Người tạo</TableCell>
                <TableCell>Trạng thái</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detailedItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, index) => (
                  <TableRow key={`${item.slipId}-${index}`} hover>
                    <TableCell>{formatDateTime(item.shippingDate)}</TableCell>
                    <TableCell>{item.soNumber}</TableCell>
                    <TableCell>{item.customerName}</TableCell>
                    <TableCell>{item.productName}</TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">{formatCurrency(item.exportPrice)}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(item.quantity * item.exportPrice)}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(item.taxAmount)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.shippingFee)}</TableCell>
                    <TableCell align="right" sx={{fontWeight: 'bold'}}>{formatCurrency(item.grandTotal)}</TableCell>
                    <TableCell>{item.createdBy}</TableCell>
                    <TableCell>{getOrderStatusChip(item.status)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={detailedItems.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Số dòng mỗi trang:"
        />
      </Paper>

      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" align="right">
          Tổng công nợ phải thu (theo bộ lọc):{' '}
          <Typography
            component="span"
            variant="h6"
            color="primary.main"
            sx={{ fontWeight: 'bold' }}
          >
            {formatCurrency(totalReceivable)}
          </Typography>
        </Typography>
      </Paper>
    </Box>
  );
}

export default CongNoXuatPage;
